import { BASE_PERMISSION } from '@/lib/constants/modules';
import type { UserRole } from '@/lib/constants/roles';
import { hasPermission } from '@/lib/permissions/store';
import type { PermissionMap } from '@/types/api';
import { isGroup, type NavItem, type NavLeaf } from './nav-config';

interface FilterContext {
  role: UserRole | null;
  isSubSuperAdmin: boolean;
  perms: PermissionMap;
  /**
   * The currently logged-in user's assigned moduleIds. `null` means
   * "skip module gating" — set this for SADMIN / SUB_SUPER_ADMIN.
   */
  modules: Set<number> | null;
}

function moduleSatisfies(
  item: { requiresAnyModuleId?: number[] },
  modules: Set<number> | null,
): boolean {
  // No requirement → always passes.
  if (!item.requiresAnyModuleId || item.requiresAnyModuleId.length === 0) {
    return true;
  }
  // `null` modules = SADMIN / SUB_SADMIN — skip module gating entirely.
  if (modules === null) return true;
  return item.requiresAnyModuleId.some((id) => modules.has(id));
}

function permissionSatisfies(
  item: NavItem | NavLeaf,
  ctx: FilterContext,
): boolean {
  if (!ctx.isSubSuperAdmin) return true;
  if (!item.permissionPath) return true;
  return hasPermission(ctx.perms, item.permissionPath, BASE_PERMISSION.VIEW);
}

export function filterNav(items: NavItem[], ctx: FilterContext): NavItem[] {
  if (!ctx.role) return [];
  return items
    .map((item) => {
      if (!item.roles.includes(ctx.role!)) return null;
      if (!permissionSatisfies(item, ctx)) return null;
      if (!moduleSatisfies(item, ctx.modules)) return null;

      if (isGroup(item)) {
        const filteredChildren = item.children.filter((child) => {
          if (!child.roles.includes(ctx.role!)) return false;
          if (!permissionSatisfies(child, ctx)) return false;
          if (!moduleSatisfies(child, ctx.modules)) return false;
          return true;
        });
        if (filteredChildren.length === 0) return null;
        return { ...item, children: filteredChildren } satisfies NavItem;
      }

      return item;
    })
    .filter((x): x is NavItem => x !== null);
}
